import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { postingClient, type Lane, type VinDecodeResponse } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'

interface MileagePreviewData {
  estDistanceMiles: number | null
  mileageProvider: string
  mileageSource: string
  mileageDurationSeconds?: number | null
  customerRatePerMileCents: number | null
  carrierRatePerMileCents: number | null
  marginPerMileCents: number | null
  warnings: string[]
}

interface AdditionalQuoteVehicle {
  vin: string
  year: string
  make: string
  model: string
  type: string
  trim: string
  engine: string
  fuelType: string
}

function normalizeVinInput(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17)
}

function isValidVinFormat(value: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(value)
}

function normalizeDecodedVehicleType(decoded: VinDecodeResponse): string | null {
  const fromApi = (decoded.vehicleType || decoded.bodyClass || '').toUpperCase()
  if (!fromApi) return null
  if (fromApi.includes('SUV') || fromApi.includes('SPORT UTILITY')) return 'SUV'
  if (fromApi.includes('TRUCK') || fromApi.includes('PICKUP')) return 'TRUCK'
  if (fromApi.includes('VAN')) return 'VAN'
  if (fromApi.includes('MOTORCYCLE')) return 'MOTORCYCLE'
  if (fromApi.includes('SEDAN') || fromApi.includes('COUPE') || fromApi.includes('WAGON') || fromApi.includes('HATCHBACK')) return 'SEDAN'
  return 'OTHER'
}

export default function QuoteNewPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [lanes, setLanes] = useState<Lane[]>([])
  const [selectedLaneId, setSelectedLaneId] = useState('')
  const [hierarchyAccounts, setHierarchyAccounts] = useState<Array<{ id: string; legalName: string; displayName?: string | null }>>([])
  const [hierarchyLocations, setHierarchyLocations] = useState<Array<{ id: string; accountId: string; name: string }>>([])
  const [hierarchyContacts, setHierarchyContacts] = useState<Array<{ id: string; accountId: string; fullName: string }>>([])
  const [hierarchyBillingProfiles, setHierarchyBillingProfiles] = useState<Array<{ id: string; accountId: string; profileName: string }>>([])
  const [accountHierarchyId, setAccountHierarchyId] = useState('')
  const [locationHierarchyId, setLocationHierarchyId] = useState('')
  const [contactHierarchyId, setContactHierarchyId] = useState('')
  const [billingProfileId, setBillingProfileId] = useState('')

  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerReference, setCustomerReference] = useState('')
  const [internalReference, setInternalReference] = useState('')

  const [originZip, setOriginZip] = useState('')
  const [originCity, setOriginCity] = useState('')
  const [originState, setOriginState] = useState('')
  const [destinationZip, setDestinationZip] = useState('')
  const [destinationCity, setDestinationCity] = useState('')
  const [destinationState, setDestinationState] = useState('')

  const [vin, setVin] = useState('')
  const [vinStatus, setVinStatus] = useState('')
  const [vinDecodeMessage, setVinDecodeMessage] = useState<string | null>(null)
  const [vinDecodeLoading, setVinDecodeLoading] = useState(false)
  const lastDecodedVinRef = useRef<string>('')

  const [vehicleYear, setVehicleYear] = useState('')
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleType, setVehicleType] = useState('')
  const [vehicleTrim, setVehicleTrim] = useState('')
  const [vehicleEngine, setVehicleEngine] = useState('')
  const [vehicleFuelType, setVehicleFuelType] = useState('')
  const [additionalVehicles, setAdditionalVehicles] = useState<AdditionalQuoteVehicle[]>([])

  const [operable, setOperable] = useState(true)
  const [transportType, setTransportType] = useState('OPEN')
  const [requestedPickupDate, setRequestedPickupDate] = useState('')
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('')
  const [pickupWindowStart, setPickupWindowStart] = useState('')
  const [pickupWindowEnd, setPickupWindowEnd] = useState('')
  const [deliveryWindowStart, setDeliveryWindowStart] = useState('')
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState('')

  const [manualMiles, setManualMiles] = useState('')
  const [mileageLoading, setMileageLoading] = useState(false)
  const [mileageData, setMileageData] = useState<MileagePreviewData | null>(null)

  const [customerQuote, setCustomerQuote] = useState('')
  const [targetCarrierPay, setTargetCarrierPay] = useState('')

  const [internalNotes, setInternalNotes] = useState('')
  const [pricingNotes, setPricingNotes] = useState('')
  const [publicNotes, setPublicNotes] = useState('')

  const originZipLookup = useZipLocation({
    zip: originZip,
    onCity: setOriginCity,
    onState: setOriginState,
    lookupZip: api.geo.zipLookup,
  })
  const destinationZipLookup = useZipLocation({
    zip: destinationZip,
    onCity: setDestinationCity,
    onState: setDestinationState,
    lookupZip: api.geo.zipLookup,
  })

  useEffect(() => {
    const loadLanes = async () => {
      try {
        const [response, hierarchyRes] = await Promise.all([
          api.lanes.list({ status: 'ACTIVE' }),
          api.accountHierarchy.search(),
        ])
        setLanes(response.data)
        setHierarchyAccounts(hierarchyRes.data.accounts.map((item) => ({ id: item.id, legalName: item.legalName, displayName: item.displayName })))
        setHierarchyLocations(hierarchyRes.data.locations.map((item) => ({ id: item.id, accountId: item.accountId, name: item.name })))
        setHierarchyContacts(hierarchyRes.data.contacts.map((item) => ({ id: item.id, accountId: item.accountId, fullName: item.fullName })))
        setHierarchyBillingProfiles(hierarchyRes.data.billingProfiles.map((item) => ({ id: item.id, accountId: item.accountId, profileName: item.profileName })))
      } catch {
        setLanes([])
      }
    }
    void loadLanes()
  }, [])

  const [touchedVehicleFields, setTouchedVehicleFields] = useState({
    year: false,
    make: false,
    model: false,
    type: false,
    trim: false,
    engine: false,
    fuel: false,
  })

  const customerQuoteNum = customerQuote ? Number(customerQuote) : null
  const targetCarrierPayNum = targetCarrierPay ? Number(targetCarrierPay) : null
  const manualMilesNum = manualMiles ? Number(manualMiles) : null
  const hasManualMilesOverride = typeof manualMilesNum === 'number' && Number.isFinite(manualMilesNum) && manualMilesNum >= 0
  const estDistanceMilesNum = hasManualMilesOverride ? manualMilesNum : mileageData?.estDistanceMiles ?? null

  const grossMargin =
    typeof customerQuoteNum === 'number' && Number.isFinite(customerQuoteNum) &&
    typeof targetCarrierPayNum === 'number' && Number.isFinite(targetCarrierPayNum)
      ? customerQuoteNum - targetCarrierPayNum
      : null
  const marginPercent = grossMargin !== null && customerQuoteNum && customerQuoteNum > 0
    ? (grossMargin / customerQuoteNum) * 100
    : null
  const customerRpm =
    typeof customerQuoteNum === 'number' && Number.isFinite(customerQuoteNum) &&
    typeof estDistanceMilesNum === 'number' && Number.isFinite(estDistanceMilesNum) && estDistanceMilesNum > 0
      ? customerQuoteNum / estDistanceMilesNum
      : null
  const carrierRpm =
    typeof targetCarrierPayNum === 'number' && Number.isFinite(targetCarrierPayNum) &&
    typeof estDistanceMilesNum === 'number' && Number.isFinite(estDistanceMilesNum) && estDistanceMilesNum > 0
      ? targetCarrierPayNum / estDistanceMilesNum
      : null

  const decodeVin = async (normalizedVin: string) => {
    if (!isValidVinFormat(normalizedVin)) {
      setVinStatus('INVALID')
      setVinDecodeMessage('Invalid VIN format')
      return
    }
    setVinDecodeLoading(true)
    setVinDecodeMessage('Decoding VIN...')
    try {
      const response = await api.vehicles.decodeVin(normalizedVin)
      const decoded = response.data
      if (!decoded.valid) {
        setVinStatus('UNDECODABLE')
        setVinDecodeMessage(decoded.error || 'VIN could not be decoded')
        return
      }
      setVinStatus('DECODED')
      setVinDecodeMessage('VIN decoded')
      if (!touchedVehicleFields.year && decoded.year) setVehicleYear(decoded.year)
      if (!touchedVehicleFields.make && decoded.make) setVehicleMake(decoded.make)
      if (!touchedVehicleFields.model && decoded.model) setVehicleModel(decoded.model)
      if (!touchedVehicleFields.type) {
        const normalizedType = normalizeDecodedVehicleType(decoded)
        if (normalizedType) setVehicleType(normalizedType)
      }
      if (!touchedVehicleFields.trim && decoded.trim) setVehicleTrim(decoded.trim)
      if (!touchedVehicleFields.engine && decoded.engine) setVehicleEngine(decoded.engine)
      if (!touchedVehicleFields.fuel && decoded.fuelType) setVehicleFuelType(decoded.fuelType)
    } catch (err: any) {
      setVinStatus('ERROR')
      setVinDecodeMessage(err?.response?.data?.error ?? 'VIN could not be decoded')
    } finally {
      setVinDecodeLoading(false)
    }
  }

  const handleVinChange = (rawValue: string) => {
    const normalized = normalizeVinInput(rawValue)
    setVin(normalized)
    if (!normalized) {
      setVinStatus('')
      setVinDecodeMessage(null)
      lastDecodedVinRef.current = ''
      return
    }
    if (normalized.length < 17) {
      setVinStatus('INVALID')
      setVinDecodeMessage('Invalid VIN format')
      lastDecodedVinRef.current = ''
      return
    }
    if (lastDecodedVinRef.current === normalized) return
    lastDecodedVinRef.current = normalized
    void decodeVin(normalized)
  }

  const handleCalculateMileage = async () => {
    if (!originZip.match(/^\d{5}(-\d{4})?$/) || !destinationZip.match(/^\d{5}(-\d{4})?$/)) {
      setError('Please enter valid 5-digit ZIPs for both origin and destination')
      return
    }
    setMileageLoading(true)
    setError(null)
    try {
      const res = await postingClient.post<MileagePreviewData>('/loads/mileage-preview', {
        originZip: originZip.trim(),
        originCity: originCity || undefined,
        originState: originState || undefined,
        destinationZip: destinationZip.trim(),
        destinationCity: destinationCity || undefined,
        destinationState: destinationState || undefined,
        customerPayCents: customerQuoteNum ? Math.round(customerQuoteNum * 100) : undefined,
        carrierPayCents: targetCarrierPayNum ? Math.round(targetCarrierPayNum * 100) : undefined,
        manualMiles: null,
      })
      setMileageData(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to calculate mileage')
    } finally {
      setMileageLoading(false)
    }
  }

  const handleSaveQuote = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!originZip || !destinationZip) {
      setError('Origin and destination ZIPs are required.')
      return
    }
    if (additionalVehicles.some((vehicle) => !vehicle.year.trim() || !vehicle.make.trim() || !vehicle.model.trim())) {
      setError('Add a year, make, and model for every additional vehicle, or remove the unused row.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        status: 'NEW',
        customerName,
        customerPhone,
        customerEmail,
        customerReference,
        internalReference,
        accountHierarchyId: accountHierarchyId || undefined,
        locationHierarchyId: locationHierarchyId || undefined,
        contactHierarchyId: contactHierarchyId || undefined,
        billingProfileId: billingProfileId || undefined,
        originZip,
        originCity,
        originState,
        destinationZip,
        destinationCity,
        destinationState,
        vin,
        vinStatus,
        vehicleYear: vehicleYear ? Number(vehicleYear) : undefined,
        vehicleMake,
        vehicleModel,
        vehicleType,
        vehicleTrim,
        vehicleEngine,
        vehicleFuelType,
        operable,
        transportType,
        requestedPickupDate: requestedPickupDate || undefined,
        requestedDeliveryDate: requestedDeliveryDate || undefined,
        estDistanceMiles: estDistanceMilesNum ?? undefined,
        mileageProvider: hasManualMilesOverride ? 'manual' : mileageData?.mileageProvider,
        mileageSource: hasManualMilesOverride ? 'manual_override' : mileageData?.mileageSource,
        customerQuote,
        targetCarrierPay,
        customerQuoteCents: customerQuoteNum != null ? Math.round(customerQuoteNum * 100) : undefined,
        targetCarrierPayCents: targetCarrierPayNum != null ? Math.round(targetCarrierPayNum * 100) : undefined,
        grossMarginCents: grossMargin != null ? Math.round(grossMargin * 100) : undefined,
        marginPercent: marginPercent != null ? marginPercent / 100 : undefined,
        customerRatePerMileCents: customerRpm != null ? Math.round(customerRpm * 100) : undefined,
        carrierRatePerMileCents: carrierRpm != null ? Math.round(carrierRpm * 100) : undefined,
        marginPerMileCents: customerRpm != null && carrierRpm != null ? Math.round((customerRpm - carrierRpm) * 100) : undefined,
        internalNotes,
        pricingNotes,
        publicNotes,
        metadata: {
            schedule: { pickupWindowStart: pickupWindowStart || null, pickupWindowEnd: pickupWindowEnd || null, deliveryWindowStart: deliveryWindowStart || null, deliveryWindowEnd: deliveryWindowEnd || null },
            vehicles: [
            {
              vin,
              year: vehicleYear ? Number(vehicleYear) : undefined,
              make: vehicleMake,
              model: vehicleModel,
              bodyStyle: vehicleType,
              trim: vehicleTrim,
              engine: vehicleEngine,
              fuelType: vehicleFuelType,
              operable,
              transportType,
            },
            ...additionalVehicles.map((vehicle) => ({
              vin: vehicle.vin,
              year: vehicle.year ? Number(vehicle.year) : undefined,
              make: vehicle.make,
              model: vehicle.model,
              bodyStyle: vehicle.type,
              trim: vehicle.trim,
              engine: vehicle.engine,
              fuelType: vehicle.fuelType,
              operable: true,
              transportType,
            })),
          ],
        },
      }
      const response = await api.quotes.create(payload as any)
      navigate(`/quotes/${response.data.quote.id}`)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save quote')
    } finally {
      setSaving(false)
    }
  }

  const handleLanePrefill = (laneId: string) => {
    setSelectedLaneId(laneId)
    if (!laneId) return
    const lane = lanes.find((entry) => entry.id === laneId)
    if (!lane) return

    if (lane.customerName) setCustomerName(lane.customerName)
    if (lane.originZip) setOriginZip(lane.originZip)
    if (lane.originCity) setOriginCity(lane.originCity)
    if (lane.originState) setOriginState(lane.originState)
    if (lane.destinationZip) setDestinationZip(lane.destinationZip)
    if (lane.destinationCity) setDestinationCity(lane.destinationCity)
    if (lane.destinationState) setDestinationState(lane.destinationState)
    if (lane.estDistanceMiles != null) setManualMiles(String(lane.estDistanceMiles))
    if (lane.transportType) setTransportType(lane.transportType)
    if (lane.targetCustomerPayCents != null) setCustomerQuote((lane.targetCustomerPayCents / 100).toFixed(2))
    if (lane.targetCarrierPayCents != null) setTargetCarrierPay((lane.targetCarrierPayCents / 100).toFixed(2))
    if (lane.notes && !internalNotes) setInternalNotes(lane.notes)
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900">New Quote</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">Create internal quote intake and pricing. Quotes are not loads.</p>

      {error && <div className="mb-3 rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      <form onSubmit={handleSaveQuote} className="space-y-5">
        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <h2 className="md:col-span-2 font-semibold">Lane Prefill</h2>
          <select className="border rounded px-3 py-2 text-sm md:col-span-2" value={selectedLaneId} onChange={e => handleLanePrefill(e.target.value)}>
            <option value="">Select lane (optional)</option>
            {lanes.map((lane) => (
              <option key={lane.id} value={lane.id}>
                {lane.laneNumber} · {lane.customerName ?? 'No customer'} · {lane.originZip ?? '-'} to {lane.destinationZip ?? '-'}
              </option>
            ))}
          </select>
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold">Additional Vehicles</h2>
              <p className="text-xs text-gray-500 mt-1">Add another vehicle when this quote contains more than one unit.</p>
            </div>
            <button
              type="button"
              className="border rounded px-3 py-2 text-sm"
              onClick={() => setAdditionalVehicles((vehicles) => [...vehicles, { vin: '', year: '', make: '', model: '', type: '', trim: '', engine: '', fuelType: '' }])}
            >
              + Add vehicle
            </button>
          </div>
          {additionalVehicles.map((vehicle, index) => {
            const updateAdditional = (key: keyof AdditionalQuoteVehicle, value: string) => {
              setAdditionalVehicles((vehicles) => vehicles.map((entry, vehicleIndex) => vehicleIndex === index ? { ...entry, [key]: value } : entry))
            }
            return (
              <div key={index} className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-3 border-t pt-3">
                <div className="md:col-span-4 flex items-center justify-between">
                  <span className="text-sm font-medium">Vehicle {index + 2}</span>
                  <button type="button" className="text-sm text-red-700" onClick={() => setAdditionalVehicles((vehicles) => vehicles.filter((_, vehicleIndex) => vehicleIndex !== index))}>Remove</button>
                </div>
                <input className="border rounded px-3 py-2 text-sm" placeholder={`Vehicle ${index + 2} VIN`} value={vehicle.vin} onChange={(e) => updateAdditional('vin', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder={`Vehicle ${index + 2} year`} value={vehicle.year} onChange={(e) => updateAdditional('year', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder={`Vehicle ${index + 2} make`} value={vehicle.make} onChange={(e) => updateAdditional('make', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder={`Vehicle ${index + 2} model`} value={vehicle.model} onChange={(e) => updateAdditional('model', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder="Body type" value={vehicle.type} onChange={(e) => updateAdditional('type', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder="Trim" value={vehicle.trim} onChange={(e) => updateAdditional('trim', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder="Engine" value={vehicle.engine} onChange={(e) => updateAdditional('engine', e.target.value)} />
                <input className="border rounded px-3 py-2 text-sm" placeholder="Fuel type" value={vehicle.fuelType} onChange={(e) => updateAdditional('fuelType', e.target.value)} />
              </div>
            )
          })}
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <h2 className="md:col-span-2 font-semibold">Customer</h2>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={accountHierarchyId}
            onChange={(e) => {
              const nextId = e.target.value
              setAccountHierarchyId(nextId)
              setLocationHierarchyId('')
              setContactHierarchyId('')
              setBillingProfileId('')
            }}
          >
            <option value="">Account hierarchy (optional)</option>
            {hierarchyAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.displayName || a.legalName}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={locationHierarchyId} onChange={(e) => setLocationHierarchyId(e.target.value)}>
            <option value="">Location (optional)</option>
            {hierarchyLocations.filter((l) => !accountHierarchyId || l.accountId === accountHierarchyId).map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={contactHierarchyId} onChange={(e) => setContactHierarchyId(e.target.value)}>
            <option value="">Requestor contact (optional)</option>
            {hierarchyContacts.filter((c) => !accountHierarchyId || c.accountId === accountHierarchyId).map((c) => (
              <option key={c.id} value={c.id}>{c.fullName}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={billingProfileId} onChange={(e) => setBillingProfileId(e.target.value)}>
            <option value="">Billing profile (optional)</option>
            {hierarchyBillingProfiles.filter((b) => !accountHierarchyId || b.accountId === accountHierarchyId).map((b) => (
              <option key={b.id} value={b.id}>{b.profileName}</option>
            ))}
          </select>
          <input className="border rounded px-3 py-2 text-sm" placeholder="Customer Name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Customer Reference" value={customerReference} onChange={e => setCustomerReference(e.target.value)} />
          <input className="md:col-span-2 border rounded px-3 py-2 text-sm" placeholder="Internal Reference" value={internalReference} onChange={e => setInternalReference(e.target.value)} />
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <h2 className="md:col-span-2 font-semibold">Route</h2>
          <input className="border rounded px-3 py-2 text-sm" placeholder="30301" maxLength={10} inputMode="numeric" value={originZip} onChange={e => setOriginZip(normalizeZipInput(e.target.value))} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Atlanta" value={originCity} onChange={e => setOriginCity(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="GA" maxLength={2} value={originState} onChange={e => setOriginState(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="33101" maxLength={10} inputMode="numeric" value={destinationZip} onChange={e => setDestinationZip(normalizeZipInput(e.target.value))} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Miami" value={destinationCity} onChange={e => setDestinationCity(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="FL" maxLength={2} value={destinationState} onChange={e => setDestinationState(e.target.value)} />
          {(originZipLookup.loading || destinationZipLookup.loading || originZipLookup.message || destinationZipLookup.message) && (
            <div className="md:col-span-2 text-xs text-gray-600" role="status">
              {originZipLookup.loading || destinationZipLookup.loading ? 'Looking up ZIP...' : originZipLookup.message ?? destinationZipLookup.message}
            </div>
          )}
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <h2 className="md:col-span-2 font-semibold">Vehicle</h2>
          <input className="border rounded px-3 py-2 text-sm md:col-span-2" placeholder="1HGBH41JXMN109186" maxLength={17} value={vin} onChange={e => handleVinChange(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="2023" value={vehicleYear} onChange={e => { setTouchedVehicleFields(v => ({ ...v, year: true })); setVehicleYear(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Toyota" value={vehicleMake} onChange={e => { setTouchedVehicleFields(v => ({ ...v, make: true })); setVehicleMake(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Camry" value={vehicleModel} onChange={e => { setTouchedVehicleFields(v => ({ ...v, model: true })); setVehicleModel(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="SUV" value={vehicleType} onChange={e => { setTouchedVehicleFields(v => ({ ...v, type: true })); setVehicleType(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Trim" value={vehicleTrim} onChange={e => { setTouchedVehicleFields(v => ({ ...v, trim: true })); setVehicleTrim(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Engine" value={vehicleEngine} onChange={e => { setTouchedVehicleFields(v => ({ ...v, engine: true })); setVehicleEngine(e.target.value) }} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Fuel Type" value={vehicleFuelType} onChange={e => { setTouchedVehicleFields(v => ({ ...v, fuel: true })); setVehicleFuelType(e.target.value) }} />
          <select className="border rounded px-3 py-2 text-sm" value={operable ? 'OPERABLE' : 'INOPERABLE'} onChange={e => setOperable(e.target.value === 'OPERABLE')}>
            <option value="OPERABLE">Operable</option>
            <option value="INOPERABLE">Inoperable</option>
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={transportType} onChange={e => setTransportType(e.target.value)}>
            <option value="OPEN">Open</option>
            <option value="ENCLOSED">Enclosed</option>
          </select>
          {(vinDecodeLoading || vinDecodeMessage) && (
            <div className="md:col-span-2 text-xs text-gray-600">{vinDecodeLoading ? 'Decoding VIN...' : vinDecodeMessage}</div>
          )}
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <h2 className="md:col-span-3 font-semibold">Pricing & Mileage</h2>
          <input className="border rounded px-3 py-2 text-sm" placeholder="Customer Quote ($)" value={customerQuote} onChange={e => setCustomerQuote(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Target Carrier Pay ($)" value={targetCarrierPay} onChange={e => setTargetCarrierPay(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" placeholder="Manual Miles Override" value={manualMiles} onChange={e => setManualMiles(e.target.value)} />
          <div className="text-sm text-gray-600">Distance: {estDistanceMilesNum ?? '—'} mi</div>
          <div className="text-sm text-gray-600">Customer RPM: {customerRpm == null ? '—' : `$${customerRpm.toFixed(2)}/mi`}</div>
          <div className="text-sm text-gray-600">Carrier RPM: {carrierRpm == null ? '—' : `$${carrierRpm.toFixed(2)}/mi`}</div>
          <div className="text-sm text-gray-600">Margin: {grossMargin == null ? '—' : `$${grossMargin.toFixed(2)}`}</div>
          <div className="text-sm text-gray-600">Margin %: {marginPercent == null ? '—' : `${marginPercent.toFixed(2)}%`}</div>
          <button type="button" className="border rounded px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700" onClick={handleCalculateMileage} disabled={mileageLoading}>
            {mileageLoading ? 'Calculating...' : 'Calculate Routed Miles'}
          </button>
          <label className="text-sm text-gray-700">Pickup date<input type="date" className="border rounded px-3 py-2 text-sm w-full" value={requestedPickupDate} onChange={e => setRequestedPickupDate(e.target.value)} /></label>
          <label className="text-sm text-gray-700">Delivery date<input type="date" className="border rounded px-3 py-2 text-sm w-full" value={requestedDeliveryDate} onChange={e => setRequestedDeliveryDate(e.target.value)} /></label>
          <label className="text-sm text-gray-700">Pickup window start<input type="datetime-local" className="border rounded px-3 py-2 text-sm w-full" value={pickupWindowStart} onChange={e => setPickupWindowStart(e.target.value)} /></label>
          <label className="text-sm text-gray-700">Pickup window end<input type="datetime-local" className="border rounded px-3 py-2 text-sm w-full" value={pickupWindowEnd} onChange={e => setPickupWindowEnd(e.target.value)} /></label>
          <label className="text-sm text-gray-700">Delivery window start<input type="datetime-local" className="border rounded px-3 py-2 text-sm w-full" value={deliveryWindowStart} onChange={e => setDeliveryWindowStart(e.target.value)} /></label>
          <label className="text-sm text-gray-700">Delivery window end<input type="datetime-local" className="border rounded px-3 py-2 text-sm w-full" value={deliveryWindowEnd} onChange={e => setDeliveryWindowEnd(e.target.value)} /></label>
        </section>

        <section className="bg-white border rounded-xl p-4 grid grid-cols-1 gap-3">
          <h2 className="font-semibold">Notes</h2>
          <textarea className="border rounded px-3 py-2 text-sm" rows={2} placeholder="Internal Notes" value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />
          <textarea className="border rounded px-3 py-2 text-sm" rows={2} placeholder="Pricing Notes" value={pricingNotes} onChange={e => setPricingNotes(e.target.value)} />
          <textarea className="border rounded px-3 py-2 text-sm" rows={2} placeholder="Public Notes" value={publicNotes} onChange={e => setPublicNotes(e.target.value)} />
        </section>

        <div className="flex gap-2">
          <button type="submit" disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">
            {saving ? 'Saving Quote...' : 'Save Quote'}
          </button>
          <button type="button" className="border px-4 py-2 rounded-lg text-sm" onClick={() => navigate('/quotes')}>Cancel</button>
        </div>
      </form>
    </div>
  )
}
